import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../lib/supabase';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiBookOpen, FiCalendar, FiUser, FiSave, FiRefreshCw, FiAlertCircle, FiCheckCircle } = FiIcons;

const StatsAdmin = () => {
  const [stats, setStats] = useState({
    republican_patriots: '24847',
    constitutional_lessons: '378',
    gop_events_monthly: '1293',
    republican_candidates: '412'
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Fetch current stats on load
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const { data, error } = await supabase
        .from('platform_stats')
        .select('stat_name,stat_value');
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const newStats = { ...stats };
        data.forEach(stat => {
          // Fixed: Using Object.prototype.hasOwnProperty.call instead of direct method access
          if (Object.prototype.hasOwnProperty.call(newStats, stat.stat_name)) {
            newStats[stat.stat_name] = stat.stat_value;
          }
        });
        setStats(newStats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setMessage({ type: 'error', text: 'Failed to load current statistics. Using default values.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    setStats({ ...stats, [name]: numericValue });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      // Create stats table if it doesn't exist
      await supabase.rpc('create_stats_table_if_not_exists');

      // Prepare upsert data
      const upsertData = Object.entries(stats).map(([stat_name, stat_value]) => ({
        stat_name,
        stat_value
      }));
      
      const { error } = await supabase
        .from('platform_stats')
        .upsert(upsertData, { onConflict: 'stat_name' });
      
      if (error) throw error;
      
      setMessage({ type: 'success', text: 'Statistics updated successfully!' });
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
    } catch (err) {
      console.error('Error saving stats:', err);
      setMessage({ type: 'error', text: 'Failed to update statistics. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-patriot-navy">Platform Statistics</h2>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="flex items-center text-patriot-red hover:text-patriot-red-dark disabled:text-gray-400"
        >
          <SafeIcon icon={FiRefreshCw} className={`h-5 w-5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {message.text && (
        <div
          className={`mb-4 p-4 rounded-lg flex items-center ${
            message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}
        >
          <SafeIcon
            icon={message.type === 'error' ? FiAlertCircle : FiCheckCircle}
            className={`h-5 w-5 mr-2 ${message.type === 'error' ? 'text-red-500' : 'text-green-500'}`}
          />
          <span>{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="republican_patriots" className="block text-sm font-medium text-gray-700 mb-1">
              Republican Patriots
            </label>
            <div className="relative">
              <SafeIcon
                icon={FiUsers}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"
              />
              <input
                type="text"
                id="republican_patriots"
                name="republican_patriots"
                value={stats.republican_patriots}
                onChange={handleChange}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                placeholder="Enter number"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Formatted display: {parseInt(stats.republican_patriots).toLocaleString()}
            </p>
          </div>

          <div>
            <label htmlFor="constitutional_lessons" className="block text-sm font-medium text-gray-700 mb-1">
              Constitutional Lessons
            </label>
            <div className="relative">
              <SafeIcon
                icon={FiBookOpen}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"
              />
              <input
                type="text"
                id="constitutional_lessons"
                name="constitutional_lessons"
                value={stats.constitutional_lessons}
                onChange={handleChange}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                placeholder="Enter number"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Formatted display: {parseInt(stats.constitutional_lessons).toLocaleString()}
            </p>
          </div>

          <div>
            <label htmlFor="gop_events_monthly" className="block text-sm font-medium text-gray-700 mb-1">
              GOP Events Monthly
            </label>
            <div className="relative">
              <SafeIcon
                icon={FiCalendar}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"
              />
              <input
                type="text"
                id="gop_events_monthly"
                name="gop_events_monthly"
                value={stats.gop_events_monthly}
                onChange={handleChange}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                placeholder="Enter number"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Formatted display: {parseInt(stats.gop_events_monthly).toLocaleString()}
            </p>
          </div>

          <div>
            <label htmlFor="republican_candidates" className="block text-sm font-medium text-gray-700 mb-1">
              Republican Candidates
            </label>
            <div className="relative">
              <SafeIcon
                icon={FiUser}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"
              />
              <input
                type="text"
                id="republican_candidates"
                name="republican_candidates"
                value={stats.republican_candidates}
                onChange={handleChange}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                placeholder="Enter number"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Formatted display: {parseInt(stats.republican_candidates).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200 disabled:bg-gray-400"
          >
            <SafeIcon icon={FiSave} className="h-5 w-5 mr-2" />
            {saving ? 'Updating...' : 'Update Statistics'}
          </button>
        </div>
      </form>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="font-medium text-gray-700 mb-2">Important Notes:</h3>
        <ul className="text-sm text-gray-600 space-y-1 list-disc pl-5">
          <li>These statistics are displayed on the homepage and other key sections of the website.</li>
          <li>Use accurate numbers to maintain transparency with users.</li>
          <li>Statistics are automatically formatted with commas for display (e.g., 24,847).</li>
          <li>Only numeric values are accepted (no commas, periods, or other characters).</li>
        </ul>
      </div>
    </motion.div>
  );
};

export default StatsAdmin;